import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Check, X, Edit2, Circle, CheckCircle, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTasks } from '../../../lib/context/TaskContext';
import type { WidgetProps } from '../../../types/dashboard';
import type { Task } from '../../../types/database';

const priorityColors = {
  low: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  high: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
};

export const TasksWidget: React.FC<WidgetProps> = () => {
  const { tasks, loading, addTask, toggleTask, deleteTask, editTask } = useTasks();
  const [newTask, setNewTask] = useState('');
  const [editingTask, setEditingTask] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleAddTask = async () => {
    if (!newTask.trim()) return;
    
    try {
      await addTask(newTask.trim());
      setNewTask('');
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add task');
    }
  };

  const handleToggleTask = async (taskId: string, completed: boolean) => {
    try {
      await toggleTask(taskId, completed);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    try {
      await deleteTask(taskId);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete task');
    }
  };

  const handleEditTask = async (taskId: string) => {
    try {
      setError(null);
      const task = tasks.find(t => t.id === taskId);
      if (task) {
        setEditingTask(taskId);
        setEditText(task.title);
      }
    } catch (err) {
      console.error('Error starting edit:', err);
      setError(err instanceof Error ? err.message : 'Failed to start editing task');
    }
  };

  const handleSaveEdit = async (taskId: string) => {
    try {
      setError(null);
      if (!editText.trim()) {
        throw new Error('Task title cannot be empty');
      }
      await editTask(taskId, editText.trim());
      setEditingTask(null);
      setEditText('');
    } catch (err) {
      console.error('Error saving edit:', err);
      setError(err instanceof Error ? err.message : 'Failed to save task edit');
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center space-x-2">
          <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-md">
            <Check className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="text-sm font-medium text-gray-900 dark:text-white">My Tasks</div>
        </div>
        <Link to="/calendar" className="text-xs text-indigo-600 dark:text-indigo-400 flex items-center hover:underline">
          View all <ArrowUpRight className="ml-1 h-3 w-3" />
        </Link>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-600 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="flex items-center space-x-2 mb-4">
        <input
          type="text"
          value={newTask}
          onChange={(e) => setNewTask(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAddTask()}
          placeholder="Add a new task..."
          className={`flex-1 px-3 py-2 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 transition-all text-sm ${
            loading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          disabled={loading}
        />
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleAddTask}
          disabled={loading}
          className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
        >
          <Plus className="h-4 w-4" />
        </motion.button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500 dark:text-gray-400">No tasks yet</p>
        </div>
      ) : (
        <div className="flex-1 overflow-auto space-y-2 pr-1 no-scrollbar">
        <AnimatePresence initial={false}>
          {tasks.map(task => (
            <motion.div
              key={task.id}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="p-3 bg-gray-50 dark:bg-dark-700 rounded-lg group"
            >
              {editingTask === task.id ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="flex-1 px-3 py-1 bg-white dark:bg-dark-800 border border-gray-300 dark:border-dark-600 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 text-sm"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveEdit(task.id);
                      } else if (e.key === 'Escape') {
                        setEditingTask(null);
                        setEditText('');
                      }
                    }}
                    autoFocus
                  />
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleSaveEdit(task.id)}
                    className="p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                  >
                    <CheckCircle className="h-4 w-4" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => {
                      setEditingTask(null);
                      setEditText('');
                    }}
                    className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                  >
                    <X className="h-4 w-4" />
                  </motion.button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleToggleTask(task.id, !task.completed)}
                      className="text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                    >
                      {task.completed ? (
                        <CheckCircle className="h-5 w-5" />
                      ) : (
                        <Circle className="h-5 w-5" />
                      )}
                    </motion.button>
                    <span className={`text-sm ${
                      task.completed
                        ? 'text-gray-400 dark:text-gray-500 line-through'
                        : 'text-gray-900 dark:text-white'
                    }`}>
                      {task.title}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleEditTask(task.id)}
                      className="p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400"
                    >
                      <Edit2 className="h-4 w-4" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleDeleteTask(task.id)}
                      className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                    >
                      <X className="h-4 w-4" />
                    </motion.button>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>)}
    </div>
  );
};